/**
 * FP BUNDLE — "Elegí tu combo" adentro de la ficha.
 *
 * Los tramos (1 unidad / 2 con descuento / 3) salen del DOM: cada <label data-tramo> trae
 * su cantidad, su porcentaje y su codigo. Cada tramo es una CAJA que contiene SUS propias
 * tarjetas de prenda, y se despliegan al elegirlo. Las tarjetas se repiten por tramo -el de
 * 1 tiene una, el de 2 tiene dos- pero el estado es uno solo y se comparte: la unidad 0 es
 * la misma unidad en los dos, asi cambiar de tramo no borra lo ya elegido. Todo el markup
 * viene de Liquid, no se construye desde JS.
 *
 * En el tramo de 1 unidad NO hay fila de modelo: desde la ficha del FP, una sola prenda es
 * un FP. Al bajar de 2 a 1 se fuerza la unidad al producto de la ficha, porque si no se
 * llevaria el otro modelo sin ningun control en pantalla que lo muestre.
 *
 * MODELOS: el combo puede mezclar productos. Los datos vienen agrupados por producto y
 * cada unidad guarda cual eligio, asi "llevo un FP y un Posta" cuenta como 2 unidades del
 * mismo combo. Cada producto trae su propio grupo de opciones pintado en Liquid -dos
 * modelos no tienen por que compartir colores ni talles- y el JS solo muestra el del
 * modelo elegido.
 *
 * Tres cosas que se ven raras y son a proposito:
 *
 * 1. El cajon se abre a mano con .open(). En este tema `auto_open_cart_drawer` esta en
 *    false y ni figura en config/settings_data.json, asi que el CartAddEvent refresca el
 *    carrito pero NO lo abre (assets/cart-drawer.js:28).
 *
 * 2. El descuento va en un POST aparte a cart_update_url, que se imprime SIN '.js'
 *    (snippets/scripts.liquid:324), al reves que cart_add_url.
 *
 * 3. Un solo dispatch de CartAddEvent, DESPUES de aplicar el descuento. Si se dispara
 *    antes, el carrito se pinta un instante con el precio sin descuento y despues salta:
 *    se lee como un error de precio.
 */

import { CartAddEvent } from '@theme/events';

const norm = (v) => (v == null ? '' : String(v).trim().toLowerCase());

/** Centavos -> "$3.402,00", que es como muestra la plata el resto de la tienda. */
function plata(centavos) {
  const entero = Math.floor(Math.abs(centavos) / 100);
  const resto = String(Math.abs(centavos) % 100).padStart(2, '0');
  return `$${new Intl.NumberFormat('es-UY').format(entero)},${resto}`;
}

class FPBundle extends HTMLElement {
  /** @type {Array<{rotulo: string, titulo: string, url: string, foto: string, variantes: Array<object>}>} */
  #productos = [];
  /** @type {Array<HTMLElement>} */
  #tramos = [];
  /** @type {Array<HTMLElement>} */
  #cajas = [];
  /** @type {Array<{prod: number, color: string|null, talle: string|null}>} */
  #elegido = [];
  #tramo = 0;
  #ocupado = false;

  connectedCallback() {
    // El editor de temas vuelve a correr el <script src> al recargar la seccion.
    if (this.dataset.fpListo === '1') return;
    this.dataset.fpListo = '1';

    try {
      this.#productos = JSON.parse(this.querySelector('[data-variantes]')?.textContent ?? '[]');
    } catch {
      // JSON roto: mejor un boton que nunca se habilita que un combo que agrega cualquier cosa.
      this.#productos = [];
    }
    this.#productos = this.#productos.filter((p) => p && Array.isArray(p.variantes) && p.variantes.length);
    if (!this.#productos.length) return;

    this.#tramos = Array.from(this.querySelectorAll('[data-tramo]'));
    this.#cajas = Array.from(this.querySelectorAll('[data-unidades-de]'));
    if (!this.#tramos.length || !this.#cajas.length) return;

    // El estado es UNO solo y se comparte entre tramos. Las tarjetas se repiten -el tramo
    // de 1 tiene una, el de 2 tiene dos- pero la unidad 0 es la misma unidad en los dos,
    // asi pasar de 1 a 2 no borra lo que el cliente ya eligio.
    //
    // Todas arrancan en el producto de la ficha. Que la segunda sea otro modelo es una
    // eleccion del cliente, no algo que se le imponga.
    const maximo = Math.max(1, Number(this.dataset.maximo || 1));
    this.#elegido = Array.from({ length: maximo }, () => ({ prod: 0, color: null, talle: null }));

    // PAR FIJO: cuando la oferta es "uno de cada", Liquid le pone a cada tarjeta su modelo
    // en data-fijo y no hay fila para cambiarlo. Se asigna ANTES de precargar el color,
    // porque el color disponible depende del modelo.
    for (const tarjeta of this.querySelectorAll('[data-unidad][data-fijo]')) {
      if (!(tarjeta instanceof HTMLElement)) continue;
      const i = Number(tarjeta.dataset.unidad);
      const f = Number(tarjeta.dataset.fijo);
      if (Number.isInteger(i) && Number.isInteger(f) && i < this.#elegido.length && this.#productos[f]) {
        this.#elegido[i].prod = f;
      }
    }

    // Marcar cuales se eligen del catalogo: antes de que el cliente elija no tienen
    // variantes, y el boton tiene que quedar bloqueado.
    for (const tarjeta of this.querySelectorAll('[data-unidad]')) {
      if (!(tarjeta instanceof HTMLElement) || !tarjeta.querySelector('[data-catalogo]')) continue;
      const i = Number(tarjeta.dataset.unidad);
      if (Number.isInteger(i) && i < this.#elegido.length) this.#elegido[i].esCat = true;
    }

    this.#elegido.forEach((_, i) => this.#precargarColor(i));

    this.#tramo = Number(this.dataset.predeterminado || 0);
    const radio = this.#tramos[this.#tramo]?.querySelector('input[type="radio"]');
    if (radio instanceof HTMLInputElement) radio.checked = true;

    this.addEventListener('click', this.#alTocar);
    this.addEventListener('change', this.#alCambiarTramo);
    this.addEventListener('input', this.#alBuscar);
    this.querySelector('[data-agregar]')?.addEventListener('click', () => this.#enviar('carrito'));
    this.querySelector('[data-comprar]')?.addEventListener('click', () => this.#enviar('checkout'));
    document.addEventListener('fp:color', this.#alColorDeArriba);

    this.#pintar();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#alTocar);
    this.removeEventListener('change', this.#alCambiarTramo);
    document.removeEventListener('fp:color', this.#alColorDeArriba);
  }

  /** Variantes de la unidad `i`: las del catalogo si eligio de ahi, si no las de su modelo. */
  #variantesDe(i) {
    const e = this.#elegido[i];
    if (e?.cat?.variantes) return e.cat.variantes;
    if (e?.esCat) return [];
    return this.#productos[e?.prod ?? 0]?.variantes ?? [];
  }

  /** Nombres posibles de una opcion, desde los ajustes del bloque. */
  #nombres(cual) {
    return (this.dataset[cual === 'color' ? 'nombresColor' : 'nombresTalle'] || '')
      .split(',')
      .map((x) => norm(x))
      .filter(Boolean);
  }

  /**
   * Trae un producto del catalogo y lo deja con la misma forma que los de Liquid. El
   * endpoint /products/<handle>.js NO devuelve inventory_quantity -es un dato que Shopify
   * no publica-, asi que `cant` queda en null y la guardia de "dos unidades de la misma
   * variante" no puede correr para estas: si no alcanza el stock, lo rechaza Shopify al
   * agregar y el mensaje sale por el manejador de error.
   */
  async #traerDelCatalogo(handle) {
    const p = await (await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })).json();
    const nomColor = this.#nombres('color');
    const nomTalle = this.#nombres('talle');
    const pos = (lista) => p.options.findIndex((o) => lista.includes(norm(o.name || o)));
    const iColor = pos(nomColor);
    const iTalle = pos(nomTalle);

    return {
      handle,
      titulo: p.title,
      foto: p.featured_image || '',
      // Las fotos SI vienen en este fetch, asi que no cuestan peso de pagina. La guia de
      // talles no: esa viaja desde Liquid en data-guia, porque el endpoint no da metafields.
      imagenes: Array.isArray(p.images) ? p.images.slice(0, 8) : [],
      variantes: p.variants.map((v) => ({
        id: v.id,
        color: iColor >= 0 ? v.options[iColor] ?? '' : '',
        talle: iTalle >= 0 ? v.options[iTalle] ?? '' : '',
        precio: v.price,
        disp: v.available,
        cant: null,
        sigue: false,
        foto: v.featured_image?.src || p.featured_image || '',
      })),
    };
  }

  /**
   * Deja la unidad en el primer color con stock de su modelo, para que los talles muestren
   * disponibilidad real desde el primer segundo en vez de un estado neutro que miente.
   */
  #precargarColor(i) {
    const primero = this.#variantesDe(i).find((v) => v.disp && v.color);
    this.#elegido[i].color = primero ? primero.color : null;
  }

  /**
   * El selector de color de arriba (fp-colores) arrastra SOLO a la prenda 1, y solo si esa
   * prenda esta en el modelo de la ficha: si el cliente ya la cambio a otro modelo, tocar
   * un color arriba no deberia devolversela sin avisar.
   */
  #alColorDeArriba = (evento) => {
    const color = evento.detail?.color;
    if (!color || !this.#elegido.length) return;
    if (this.#elegido[0].prod !== 0) return;
    if (norm(this.#elegido[0].color) === norm(color)) return;

    this.#elegido[0].color = color;
    // El talle elegido puede no existir en el color nuevo: se suelta en vez de quedar
    // marcado sobre una combinacion que no se puede comprar.
    const v = this.#variante(0, color, this.#elegido[0].talle);
    if (!v?.disp) this.#elegido[0].talle = null;

    this.#pintar();
  };

  get #cantidad() {
    return Number(this.#tramos[this.#tramo]?.dataset.cantidad || 1);
  }

  get #pct() {
    return Number(this.#tramos[this.#tramo]?.dataset.pct || 0);
  }

  get #codigo() {
    return (this.#tramos[this.#tramo]?.dataset.codigo || '').trim();
  }

  #alCambiarTramo = (evento) => {
    const destino = evento.target;
    if (!(destino instanceof HTMLInputElement) || destino.type !== 'radio') return;
    const etiqueta = destino.closest('[data-tramo]');
    if (!(etiqueta instanceof HTMLElement)) return;
    this.#tramo = this.#tramos.indexOf(etiqueta);
    this.#fijarModeloSiEsUnaSola();
    this.#pintar();
  };

  /**
   * En el tramo de 1 unidad no hay eleccion de modelo, asi que la prenda vuelve al producto
   * de la ficha. Sin esto, alguien que en el tramo de 2 puso la unidad 1 en el otro modelo y
   * despues baja a 1 se llevaria ese otro modelo, sin ningun control en pantalla que se lo
   * muestre: desde la ficha del FP saldria un Posta.
   */
  #fijarModeloSiEsUnaSola() {
    if (this.#cantidad !== 1 || !this.#elegido.length) return;
    // Con par fijo la unidad 0 ya esta clavada al producto de la ficha; no hay nada que
    // corregir y tocarlo borraria el color precargado.
    if (this.querySelector('[data-unidad="0"][data-fijo]')) return;
    if (this.#elegido[0].prod === 0) return;
    this.#elegido[0].prod = 0;
    this.#precargarColor(0);
    this.#elegido[0].talle = null;
  }

  #alTocar = (evento) => {
    const destino = evento.target instanceof Element ? evento.target : null;
    if (!destino) return;

    // --- catalogo: abrir, elegir, cambiar ---
    const abrir = destino.closest('[data-abrir-cat], [data-cat-cambiar]');
    if (abrir) {
      const cat = abrir.closest('[data-catalogo]');
      cat?.querySelector('[data-cat-panel]')?.removeAttribute('hidden');
      cat?.querySelector('[data-abrir-cat]')?.setAttribute('hidden', '');
      cat?.querySelector('[data-cat-elegido]')?.setAttribute('hidden', '');
      return;
    }

    const mini = destino.closest('[data-cat-foto]');
    if (mini instanceof HTMLElement) {
      const gal = mini.closest('[data-cat-galeria]');
      const grande = gal?.querySelector('[data-cat-principal] img');
      if (grande instanceof HTMLImageElement) grande.src = mini.dataset.catFoto || grande.src;
      gal?.querySelectorAll('[data-cat-foto]').forEach((x) => x.setAttribute('aria-pressed', String(x === mini)));
      return;
    }

    const pick = destino.closest('[data-pick]');
    if (pick instanceof HTMLElement) {
      const tarj = pick.closest('[data-unidad]');
      if (tarj instanceof HTMLElement) this.#elegirDelCatalogo(Number(tarj.dataset.unidad), pick.dataset.pick, pick);
      return;
    }

    const boton = destino.closest('[data-modelo], [data-color], [data-talle]');
    if (!(boton instanceof HTMLElement)) return;
    if (boton.getAttribute('aria-disabled') === 'true') return;

    const tarjeta = boton.closest('[data-unidad]');
    if (!(tarjeta instanceof HTMLElement)) return;
    // El indice sale del atributo y no de indexOf: la misma unidad aparece una vez por
    // tramo, asi que una lista global daria el indice equivocado.
    const i = Number(tarjeta.dataset.unidad);
    if (!Number.isInteger(i) || i < 0 || i >= this.#elegido.length) return;

    if (boton.dataset.modelo != null) {
      // Con par fijo no hay botones de modelo, pero si alguien los inyecta desde la consola
      // el modelo no se mueve: la oferta es "uno de cada" y el precio esta calculado asi.
      if (tarjeta.dataset.fijo != null) return;
      const prod = Number(boton.dataset.modelo);
      if (prod === this.#elegido[i].prod) return;
      // Cambiar de modelo arrastra color y talle si el modelo nuevo los tiene; si no, se
      // sueltan. Guardar un talle que el otro modelo no fabrica es peor que pedirlo de nuevo.
      const colorPrevio = this.#elegido[i].color;
      const tallePrevio = this.#elegido[i].talle;
      this.#elegido[i].prod = prod;

      const sirveColor = this.#variantesDe(i).some((v) => norm(v.color) === norm(colorPrevio) && v.disp);
      if (!sirveColor) this.#precargarColor(i);

      const v = this.#variante(i, this.#elegido[i].color, tallePrevio);
      this.#elegido[i].talle = v?.disp ? tallePrevio : null;
    } else if (boton.dataset.color != null) {
      this.#elegido[i].color = boton.dataset.color;
      const v = this.#variante(i, boton.dataset.color, this.#elegido[i].talle);
      if (!v?.disp) this.#elegido[i].talle = null;
    } else if (boton.dataset.talle != null) {
      this.#elegido[i].talle = boton.dataset.talle;
    }

    this.#pintar();
  };

  /** Filtra las tarjetas del catalogo por nombre, sin tocar el DOM mas que el hidden. */
  #alBuscar = (evento) => {
    const caja = evento.target;
    if (!(caja instanceof HTMLInputElement) || caja.dataset.catBuscar === undefined) return;
    const q = norm(caja.value);
    const grilla = caja.closest('[data-cat-panel]');
    let visibles = 0;
    grilla?.querySelectorAll('[data-pick]').forEach((c) => {
      if (!(c instanceof HTMLElement)) return;
      const entra = !q || norm(c.dataset.nombre).includes(q);
      c.hidden = !entra;
      if (entra) visibles += 1;
    });
    const vacio = grilla?.querySelector('[data-cat-vacio]');
    if (vacio instanceof HTMLElement) vacio.hidden = visibles > 0;
  };

  /** Trae la prenda elegida del catalogo y pinta sus opciones. */
  async #elegirDelCatalogo(i, handle, tarjetaOrigen) {
    if (!Number.isInteger(i) || !handle || i >= this.#elegido.length) return;
    const tarjeta = this.querySelector(`[data-unidad="${i}"] [data-catalogo]`)?.closest('[data-unidad]');
    const cat = tarjeta?.querySelector('[data-catalogo]');
    const destino = cat?.querySelector('[data-cat-elegido]');
    if (!(destino instanceof HTMLElement)) return;

    const panel = cat.querySelector('[data-cat-panel]');
    if (panel instanceof HTMLElement) panel.hidden = true;
    destino.hidden = false;
    destino.textContent = 'Cargando…';

    let datos;
    try {
      datos = await this.#traerDelCatalogo(handle);
    } catch {
      destino.textContent = 'No pudimos cargar esa prenda. Probá con otra.';
      return;
    }

    this.#elegido[i].cat = datos;
    this.#elegido[i].color = null;
    this.#elegido[i].talle = null;
    this.#precargarColor(i);

    this.#pintarCatalogo(destino, datos);

    // La guia de ESTA prenda. El dialogo ya existe en el DOM -lo pinto Liquid- y aca solo
    // se le cambia la imagen; despues se mueve al lado de la fila de talle, que es donde
    // hace falta. Mover un custom element ya montado es seguro: se desconecta y se vuelve a
    // conectar, y vuelve a tomar sus refs con los hijos que ya trae.
    const guia = destino.parentElement?.querySelector('[data-cat-guia]');
    if (guia instanceof HTMLElement) {
      const url = tarjetaOrigen instanceof HTMLElement ? tarjetaOrigen.dataset.guia || '' : '';
      const img = guia.querySelector('[data-cat-guia-img]');
      const vacia = guia.querySelector('[data-cat-guia-vacia]');
      const prenda = guia.querySelector('[data-cat-guia-prenda]');
      if (prenda) prenda.textContent = datos.titulo;
      if (img instanceof HTMLImageElement) {
        if (url) {
          img.src = url;
          img.alt = `${datos.titulo} — guía de talles`;
        }
        img.hidden = !url;
      }
      if (vacia instanceof HTMLElement) vacia.hidden = Boolean(url);
      guia.hidden = false;

      const filaTalle = destino.querySelector('[data-fila-talle]');
      if (filaTalle) filaTalle.append(guia);
    }

    this.#pintar();
  }

  /**
   * Unico lugar de todo el bloque donde se construye markup desde JS, y es inevitable: no
   * se pueden pintar en Liquid las variantes de 45 productos. Se usan las MISMAS clases y
   * los mismos data-* que el markup de Liquid, asi #pintar() no necesita saber de donde
   * salieron los botones.
   */
  #pintarCatalogo(destino, datos) {
    destino.textContent = '';

    const cabeza = document.createElement('div');
    cabeza.className = 'fp-bu__cat-cabeza';
    const nombre = document.createElement('b');
    nombre.textContent = datos.titulo;
    cabeza.append(nombre);
    const cambiar = document.createElement('button');
    cambiar.type = 'button';
    cambiar.className = 'fp-bu__cat-cambiar';
    cambiar.setAttribute('data-cat-cambiar', '');
    cambiar.textContent = this.dataset.cambiar || 'Cambiar';
    cabeza.append(cambiar);
    destino.append(cabeza);

    // Galeria de la prenda: foto grande y tira de miniaturas. Es lo que deja al cliente
    // asegurarse de cual se lleva antes de elegir el talle.
    if (datos.imagenes?.length) {
      const gal = document.createElement('div');
      gal.className = 'fp-bu__cat-galeria';
      gal.setAttribute('data-cat-galeria', '');

      const marco = document.createElement('span');
      marco.className = 'fp-bu__cat-principal';
      marco.setAttribute('data-cat-principal', '');
      const grande = document.createElement('img');
      grande.src = datos.imagenes[0];
      grande.alt = datos.titulo;
      grande.loading = 'lazy';
      marco.append(grande);
      gal.append(marco);

      if (datos.imagenes.length > 1) {
        const tira = document.createElement('div');
        tira.className = 'fp-bu__cat-tira';
        datos.imagenes.forEach((src, k) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'fp-bu__cat-mini';
          b.dataset.catFoto = src;
          b.setAttribute('aria-pressed', String(k === 0));
          b.setAttribute('aria-label', `Foto ${k + 1} de ${datos.titulo}`);
          const im = document.createElement('img');
          im.src = src;
          im.alt = '';
          im.loading = 'lazy';
          b.append(im);
          tira.append(b);
        });
        gal.append(tira);
      }
      destino.append(gal);
    }

    const grupo = (etiqueta, refDato, esTalle) => {
      const g = document.createElement('div');
      g.className = 'fp-bu__grupo';
      const ley = document.createElement('div');
      // La fila del talle lleva la guia al lado, asi que va como flex con espacio entre.
      ley.className = esTalle ? 'fp-bu__leyenda fp-bu__leyenda--conguia' : 'fp-bu__leyenda';
      if (esTalle) ley.setAttribute('data-fila-talle', '');
      const texto = document.createElement('span');
      texto.append(document.createTextNode(etiqueta + ': '));
      const b = document.createElement('b');
      b.setAttribute(refDato, '');
      b.textContent = '—';
      texto.append(b);
      ley.append(texto);
      g.append(ley);
      return g;
    };

    const colores = [...new Set(datos.variantes.map((v) => v.color).filter(Boolean))];
    if (colores.length) {
      const g = grupo(this.dataset.etiquetaColor || 'Color', 'data-elegido-color');
      const fila = document.createElement('div');
      fila.className = 'fp-bu__colores';
      for (const c of colores) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'fp-bu__color';
        b.dataset.color = c;
        b.setAttribute('aria-pressed', 'false');
        b.title = c;
        const tinta = document.createElement('span');
        tinta.className = 'fp-bu__color-tinta';
        const foto = datos.variantes.find((v) => norm(v.color) === norm(c) && v.foto)?.foto;
        if (foto) tinta.style.backgroundImage = `url(${foto})`;
        b.append(tinta);
        const oculto = document.createElement('span');
        oculto.className = 'visually-hidden';
        oculto.textContent = c;
        b.append(oculto);
        fila.append(b);
      }
      g.append(fila);
      destino.append(g);
    }

    const talles = [...new Set(datos.variantes.map((v) => v.talle).filter(Boolean))];
    if (talles.length) {
      const g = grupo(this.dataset.etiquetaTalle || 'Talle', 'data-elegido-talle', true);
      const fila = document.createElement('div');
      fila.className = 'fp-bu__talles';
      for (const s of talles) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'fp-bu__talle';
        b.dataset.talle = s;
        b.setAttribute('aria-pressed', 'false');
        b.textContent = s;
        fila.append(b);
      }
      g.append(fila);
      destino.append(g);
    }
  }

  #variante(i, color, talle) {
    return (
      this.#variantesDe(i).find((v) => {
        const okColor = !v.color || color == null || norm(v.color) === norm(color);
        const okTalle = !v.talle || talle == null || norm(v.talle) === norm(talle);
        return okColor && okTalle;
      }) ?? null
    );
  }

  /** Variante de la unidad `i`, solo si esta completa y es comprable. */
  #elegida(i) {
    const { color, talle } = this.#elegido[i];
    const vs = this.#variantesDe(i);
    if (vs.some((v) => v.color) && !color) return null;
    if (vs.some((v) => v.talle) && !talle) return null;
    const v = this.#variante(i, color, talle);
    return v && v.disp ? v : null;
  }

  /** Precio (y precio de lista) de la unidad `i` para el CARTEL del tramo: el de la variante
   *  elegida si ya la eligio, y si todavia no, el mas barato de los que puede elegir. Sin esto
   *  el cartel se queda con el precio del producto base hasta que el cliente termina de elegir
   *  todo: cambiaba el segundo pantalon por uno de $2.790 y el tramo seguia diciendo $4.990,
   *  el precio de dos del base. El carrito siempre cobro bien; lo que mentia era el cartel. */
  #precioDe(i) {
    const v = this.#elegida(i);
    if (v) return { precio: v.precio, lista: v.lista ?? v.precio };
    const todas = this.#variantesDe(i);
    const vs = todas.filter((x) => x.disp);
    const base = vs.length ? vs : todas;
    if (!base.length) return null;
    const min = base.reduce((a, b) => (b.precio < a.precio ? b : a));
    return { precio: min.precio, lista: min.lista ?? min.precio };
  }

  #activas() {
    return Array.from({ length: this.#cantidad }, (_, i) => this.#elegida(i));
  }

  #pintar() {
    // Se abre la caja del tramo elegido y se cierran las otras. Solo se pintan las tarjetas
    // de la caja abierta: las de las cerradas no se ven, y se repintan al abrirlas.
    this.#cajas.forEach((caja) => {
      const suyo = Number(caja.dataset.unidadesDe) === this.#tramo;
      caja.hidden = !suyo;
      caja.closest('[data-caja]')?.classList.toggle('is-abierta', suyo);
    });

    const abierta = this.#cajas[this.#tramo];
    if (!abierta) return;

    Array.from(abierta.querySelectorAll('[data-unidad]')).forEach((tarjeta) => {
      if (!(tarjeta instanceof HTMLElement)) return;
      const i = Number(tarjeta.dataset.unidad);
      if (!Number.isInteger(i) || i >= this.#elegido.length) return;
      tarjeta.hidden = false;

      const { prod, color, talle } = this.#elegido[i];
      const vs = this.#variantesDe(i);

      // --- modelo ---
      tarjeta.querySelectorAll('[data-modelo]').forEach((boton) => {
        if (!(boton instanceof HTMLElement)) return;
        const suyo = Number(boton.dataset.modelo);
        boton.setAttribute('aria-pressed', String(suyo === prod));

        // La foto de CADA tarjeta de modelo sigue al color elegido en esta unidad. Con la
        // foto principal de cada producto, el FP salia negro y el Posta crema solo porque
        // asi estan cargados: el cliente leia "Posta = crema" cuando el color se elige mas
        // abajo y los dos vienen en los dos. Asi la tarjeta muestra lo que se va a llevar.
        const img = boton.querySelector('img');
        if (img instanceof HTMLImageElement) {
          const suyas = this.#productos[suyo]?.variantes ?? [];
          const v =
            suyas.find((x) => norm(x.color) === norm(color) && x.foto) ?? suyas.find((x) => x.foto);
          const src = v?.foto || this.#productos[suyo]?.foto;
          if (src && img.src !== src) img.src = src;
        }
      });
      const rotuloModelo = tarjeta.querySelector('[data-elegido-modelo]');
      if (rotuloModelo) rotuloModelo.textContent = this.#productos[prod]?.rotulo || '—';

      // Solo el grupo de opciones del modelo elegido queda visible. Los otros siguen en el
      // DOM: volver a mostrarlos es gratis y no hay que repintar nada.
      const grupos = Array.from(tarjeta.querySelectorAll('[data-opciones-de]'));
      grupos.forEach((g) => {
        if (g instanceof HTMLElement) g.hidden = Number(g.dataset.opcionesDe) !== prod;
      });
      const activo = grupos.find((g) => g instanceof HTMLElement && !g.hidden) ?? tarjeta;

      // --- colores del modelo activo ---
      activo.querySelectorAll('[data-color]').forEach((boton) => {
        if (!(boton instanceof HTMLElement)) return;
        const suyo = boton.dataset.color ?? '';
        const hay = vs.some((v) => norm(v.color) === norm(suyo) && v.disp);
        boton.classList.toggle('is-agotado', !hay);
        if (hay) boton.removeAttribute('aria-disabled');
        else boton.setAttribute('aria-disabled', 'true');
        boton.setAttribute('aria-pressed', String(norm(suyo) === norm(color)));
      });

      // --- talles, segun el color elegido ---
      activo.querySelectorAll('[data-talle]').forEach((boton) => {
        if (!(boton instanceof HTMLElement)) return;
        const suyo = boton.dataset.talle ?? '';
        const v = this.#variante(i, color, suyo);
        const hay = Boolean(v && v.disp);
        boton.classList.toggle('is-agotado', !hay);
        if (hay) boton.removeAttribute('aria-disabled');
        else boton.setAttribute('aria-disabled', 'true');
        boton.setAttribute('aria-pressed', String(norm(suyo) === norm(talle)));
      });

      const rc = activo.querySelector('[data-elegido-color]');
      if (rc) rc.textContent = color || '—';
      const rt = activo.querySelector('[data-elegido-talle]');
      if (rt) rt.textContent = talle || '—';

      // --- miniatura: sigue al modelo Y al color ---
      const foto = tarjeta.querySelector('[data-foto]');
      const vFoto = this.#variante(i, color, talle) ?? this.#variante(i, color, null);
      const src = vFoto?.foto || this.#productos[prod]?.foto;
      if (foto instanceof HTMLImageElement && src) foto.src = src;

      // --- aviso por unidad ---
      const aviso = tarjeta.querySelector('[data-aviso]');
      if (aviso instanceof HTMLElement) {
        const colorMuerto = color && !vs.some((v) => norm(v.color) === norm(color) && v.disp);
        let texto = '';
        if (colorMuerto) texto = 'Este color está agotado en todos los talles.';
        else if (!this.#elegida(i)) texto = 'Elegí talle.';
        aviso.textContent = texto;
        aviso.hidden = texto === '';
      }
    });

    this.#pintarPrecio();
  }

  #pintarPrecio() {
    const activas = this.#activas();
    const completo = activas.every(Boolean);

    // El precio del tramo se recalcula con las variantes REALES solo cuando estan todas
    // elegidas. Si falta alguna, se deja el que pinto Liquid con el precio base, que es el
    // "desde" correcto. Con modelos mezclados esto importa: dos modelos pueden no costar
    // lo mismo.
    // El regalo entra en los dos lados, igual que en Liquid: a precio de lista en el
    // tachado y a precio de regalo en el total.
    const tarjetaRegalo = this.#cajas[this.#tramo]?.querySelector('[data-regalo]');
    const regaloPrecio = tarjetaRegalo ? Number(this.dataset.regaloPrecio || 0) : 0;
    const regaloLista = tarjetaRegalo ? Number(tarjetaRegalo.dataset.regaloLista || 0) : 0;

    // El cartel se repinta en cuanto se sabe QUE prenda lleva cada unidad, sin esperar al
    // talle: con prendas de distinto precio (el segundo pantalon se elige entre 42) el precio
    // de Liquid -dos veces el producto base- deja de ser cierto apenas cambian una.
    const desde = Array.from({ length: this.#cantidad }, (_, i) => this.#precioDe(i));
    if (desde.every(Boolean)) {
      // Con tachado-lista el precio que se tacha sale del compare_at de cada variante, no
      // de su precio: la prenda ya viene rebajada y el tramo no descuenta de nuevo. El
      // neto SIEMPRE se calcula sobre el precio real, nunca sobre el de lista.
      const porLista = this.dataset.tachadoLista === '1';
      const bruto =
        desde.reduce((s, p) => s + (porLista && p.lista > p.precio ? p.lista : p.precio), 0) + regaloLista;
      const neto = Math.round((desde.reduce((s, p) => s + p.precio, 0) * (100 - this.#pct)) / 100) + regaloPrecio;
      const etiqueta = this.#tramos[this.#tramo];
      const elPrecio = etiqueta?.querySelector('[data-tramo-precio]');
      const elAntes = etiqueta?.querySelector('[data-tramo-antes]');
      const elAhorro = etiqueta?.querySelector('[data-tramo-ahorro]');
      if (elPrecio) elPrecio.textContent = plata(neto);
      if (elAntes) elAntes.textContent = plata(bruto);
      if (elAhorro) {
        const ahorro = bruto - neto;
        elAhorro.textContent = `${elAhorro.dataset.tramoAhorro || 'Ahorrás'} ${plata(ahorro)}`;
        elAhorro.hidden = ahorro <= 0;
      }
      if (elAntes) elAntes.hidden = bruto <= neto;
    }

    const conflicto = this.#faltaStock(activas);
    const bloqueado = !completo || Boolean(conflicto) || this.#ocupado;
    for (const b of this.querySelectorAll('[data-agregar], [data-comprar]')) {
      if (b instanceof HTMLButtonElement) b.disabled = bloqueado;
    }

    this.#estado(conflicto ?? '', Boolean(conflicto));
  }

  /** Texto del problema de stock, o null. Varias unidades sobre la MISMA variante necesitan
   *  ese stock; si no, Shopify rechaza el add recien despues de que el cliente eligio todo. */
  #faltaStock(activas) {
    if (!activas.length || !activas.every(Boolean)) return null;

    const porVariante = new Map();
    for (const v of activas) {
      const fila = porVariante.get(v.id) ?? { pedidas: 0, v };
      fila.pedidas += 1;
      porVariante.set(v.id, fila);
    }

    for (const { pedidas, v } of porVariante.values()) {
      if (pedidas <= 1 || v.sigue) continue;
      // cant en null = variante traida del catalogo, Shopify no publica su stock.
      if (v.cant == null) continue;
      if (typeof v.cant === 'number' && v.cant >= pedidas) continue;
      const cual = [v.color, v.talle].filter(Boolean).join(' / ');
      return `Queda${v.cant === 1 ? '' : 'n'} ${v.cant} de ${cual}. Elegí otra combinación para una de las prendas.`;
    }

    return null;
  }

  #estado(texto, esError) {
    const el = this.querySelector('[data-estado]');
    if (!(el instanceof HTMLElement)) return;
    el.textContent = texto;
    el.hidden = texto === '';
    el.classList.toggle('is-error', Boolean(esError));
  }

  #secciones() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((n) => (n instanceof HTMLElement ? n.dataset.sectionId : null))
      .filter(Boolean);
  }

  /**
   * Un solo camino para los dos botones. La unica diferencia esta al final: "carrito" abre
   * el cajon y "checkout" redirige. Comparten validacion, fusion de lineas y descuento,
   * asi no puede pasar que uno aplique el codigo y el otro no.
   *
   * "Comprar ahora" NO vacia el carrito antes. Un enlace de carrito
   * (/cart/id:cantidad) lo haria, y el cliente perderia lo que ya tenia adentro sin
   * enterarse. Agrega el combo y lleva al pago con todo lo que haya.
   *
   * @param {'carrito'|'checkout'} modo
   */
  #enviar = async (modo) => {
    if (this.#ocupado) return;

    const activas = this.#activas();
    if (!activas.length || !activas.every(Boolean) || this.#faltaStock(activas)) return;

    const alPago = modo === 'checkout';
    const etiqueta = this.querySelector(alPago ? '[data-comprar-texto]' : '[data-agregar-texto]');
    const textoOriginal = etiqueta?.textContent ?? '';

    this.#ocupado = true;
    for (const b of this.querySelectorAll('[data-agregar], [data-comprar]')) {
      if (b instanceof HTMLButtonElement) b.disabled = true;
    }
    if (etiqueta) etiqueta.textContent = alPago ? 'Yendo al pago…' : 'Agregando…';
    this.#estado('', false);

    let navegando = false;

    // Varias unidades de la misma variante van como UNA linea con cantidad N: Shopify las
    // fusiona igual, y mandarlas fusionadas evita que el carrito parpadee. Dos modelos
    // distintos tienen variantes distintas, asi que entran como dos lineas.
    const porVariante = new Map();
    for (const v of activas) porVariante.set(v.id, (porVariante.get(v.id) ?? 0) + 1);
    const items = Array.from(porVariante, ([id, quantity]) => ({ id, quantity }));

    // El regalo va como una linea mas, a precio normal: el descuento BXGY de Shopify se lo
    // baja despues, cuando se aplica el codigo del tramo. Si se agregara sin el codigo, el
    // cliente pagaria el precio de lista.
    const regalo = this.#cajas[this.#tramo]?.querySelector('[data-regalo]');
    if (regalo instanceof HTMLElement) {
      const idRegalo = Number(regalo.dataset.regalo);
      if (Number.isInteger(idRegalo) && idRegalo > 0) items.push({ id: idRegalo, quantity: 1 });
    }

    const secciones = this.#secciones();
    const codigo = this.#codigo;

    try {
      const respuesta = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(secciones.length ? { items, sections: secciones.join(',') } : { items }),
      });
      const datos = await respuesta.json();
      if (datos.status || datos.description) {
        throw new Error(datos.description || datos.message || 'No se pudo agregar');
      }

      let sections = datos.sections;
      let nota = '';

      if (codigo) {
        const previo = await this.#codigoPrevio();
        const res = await this.#aplicarCodigo(codigo, secciones);
        if (res?.sections) sections = res.sections;

        const quedo = (res?.discount_codes ?? []).some((c) => c.applicable && norm(c.code) === norm(codigo));
        if (!quedo) {
          nota = 'El combo se agregó, pero el descuento no se aplicó. Revisalo antes de pagar.';
        } else if (previo && norm(previo) !== norm(codigo)) {
          nota = `Reemplazamos tu código ${previo} por el del combo. La tienda solo toma uno a la vez.`;
        }
      }

      if (alPago) {
        // Se navega con #ocupado en true a proposito: si se soltara, un doble toque en el
        // instante previo a que el navegador cambie de pagina dispararia un segundo add y
        // el combo entraria dos veces.
        const destino = this.querySelector('[data-checkout]')?.getAttribute('data-checkout') || '/checkout';
        navegando = true;
        window.location.assign(destino);
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, 'fp-bundle', { source: 'fp-bundle', itemCount: activas.length, sections })
      );

      document.querySelector('cart-drawer-component')?.open?.();

      if (nota) this.#estado(nota, false);
    } catch (error) {
      this.#estado(
        error instanceof Error && error.message ? error.message : 'No se pudo agregar. Probá de nuevo.',
        true
      );
    } finally {
      // El finally corre tambien despues del return que dispara la navegacion. Sin esta
      // guarda los botones se re-habilitan en el instante previo a que el navegador cambie
      // de pagina, y un doble toque mete el combo dos veces.
      if (!navegando) {
        this.#ocupado = false;
        if (etiqueta) etiqueta.textContent = textoOriginal;
        this.#pintarPrecio();
      }
    }
  };

  async #codigoPrevio() {
    try {
      const c = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();
      return (c.discount_codes ?? []).find((x) => x.applicable)?.code ?? null;
    } catch {
      return null;
    }
  }

  async #aplicarCodigo(codigo, secciones) {
    try {
      const r = await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codigo, sections: secciones }),
      });
      return await r.json();
    } catch {
      return null;
    }
  }
}

if (!customElements.get('fp-bundle')) {
  customElements.define('fp-bundle', FPBundle);
}
